import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkles,
  Loader2,
  RotateCcw,
  Save,
  TrendingUp,
  Lightbulb,
  Pin,
} from "lucide-react";
import { toast } from "sonner";

const STYLE_LABEL = {
  ART_DECO: "Art Deco",
  CLASSIC: "Classic",
  MODERN: "Modern",
  NEOCLASSIC: "Neoclassic",
};

const TYPE_LABEL = {
  INTERIOR: "Interior",
  EXTERIOR: "Exterior",
};

function StatPill({ icon: Icon, label, value, accent }) {
  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs ${
        accent
          ? "bg-orange-500/10 border-orange-500/30 text-orange-300"
          : "bg-zinc-900/60 border-white/10 text-zinc-300"
      }`}
    >
      <Icon size={11} />
      <span className="text-zinc-500">{label}</span>
      <strong className="text-white">{value}</strong>
    </div>
  );
}

function NeedsReviewBadge({ count }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 text-[11px] font-medium animate-pulse"
      data-testid="needs-review-badge"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
      {count} feedback nou — review necesar
    </span>
  );
}

function IssueHeatmap({ issues }) {
  if (!issues || !issues.rows || issues.rows.length === 0) {
    return (
      <div
        className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-sm text-emerald-300 flex items-center gap-2"
        data-testid="issue-heatmap-empty"
      >
        <Lightbulb size={14} />
        Nicio problemă raportată — toate stilurile arată bine până acum.
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-white/10 bg-zinc-900/30 p-5 space-y-3"
      data-testid="issue-heatmap"
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <TrendingUp size={16} className="text-orange-500" />
          Hartă probleme — {issues.total_not_ok} feedback negativ
        </h3>
        <p className="text-[11px] text-zinc-500">
          Top probleme raportate de admin/sales pe fiecare combinație tip × stil.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="issue-heatmap-table">
          <thead>
            <tr className="text-left text-zinc-500 uppercase tracking-wider">
              <th className="pb-2 pr-4 font-medium">Tip / Stil</th>
              <th className="pb-2 pr-4 font-medium">Problemă</th>
              <th className="pb-2 pr-4 font-medium text-right">Raportări</th>
            </tr>
          </thead>
          <tbody>
            {issues.rows.map((r, i) => (
              <tr
                key={`${r.sim_type}-${r.style}-${r.reason_id}-${i}`}
                className="border-t border-white/5"
              >
                <td className="py-2 pr-4 text-zinc-300">
                  <span className="text-zinc-500">{r.sim_type}</span>{" / "}
                  <span className="text-white font-medium">{r.style}</span>
                </td>
                <td className="py-2 pr-4 text-zinc-300">{r.reason_label}</td>
                <td className="py-2 pr-4 text-right">
                  <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-md bg-red-500/15 border border-red-500/30 text-red-300 font-semibold">
                    {r.count}×
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnchorCard({ item, onRefresh }) {
  const {
    sim_type,
    style,
    default_anchor,
    current_anchor,
    is_overridden,
    stats,
    updated_by,
    updated_at,
    not_ok_count = 0,
    needs_review = false,
  } = item;
  const [draft, setDraft] = useState(current_anchor);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    setDraft(current_anchor);
  }, [current_anchor]);

  const dirty = draft.trim() !== current_anchor.trim();
  const pct5 = stats.count > 0 ? Math.round((stats.count_5 / stats.count) * 100) : 0;

  const save = async () => {
    if (draft.trim().length < 10) {
      toast.error("Anchor-ul trebuie să aibă minim 10 caractere.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/prompts/${sim_type}/${style}`, { anchor: draft.trim() });
      toast.success(`Anchor salvat pentru ${TYPE_LABEL[sim_type]} / ${STYLE_LABEL[style]}`);
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare salvare");
    } finally {
      setSaving(false);
    }
  };

  const resetDefault = async () => {
    setResetting(true);
    try {
      await api.delete(`/admin/prompts/${sim_type}/${style}`);
      toast.success("Restabilit la default");
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare restabilire");
    } finally {
      setResetting(false);
    }
  };

  const autoSuggest = async () => {
    setSuggestOpen(true);
    setSuggesting(true);
    setSuggestion(null);
    try {
      const { data } = await api.post(`/admin/prompts/${sim_type}/${style}/auto-suggest`);
      setSuggestion(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare AI");
      setSuggestOpen(false);
    } finally {
      setSuggesting(false);
    }
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    setDraft(suggestion.suggested_anchor);
    setSuggestOpen(false);
    toast.message("Sugestie încărcată în editor — apasă Salvează ca să o aplici.", {
      duration: 4500,
    });
  };

  const fmtDate = updated_at ? new Date(updated_at).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }) : null;

  return (
    <div
      className={`rounded-xl border p-5 space-y-4 ${
        needs_review
          ? "border-red-500/50 bg-red-500/5"
          : is_overridden
          ? "border-orange-500/40 bg-orange-500/5"
          : "border-white/10 bg-zinc-900/50"
      }`}
      data-testid={`prompt-card-${sim_type}-${style}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {STYLE_LABEL[style]}
          </h3>
          <p className="text-xs text-zinc-500 uppercase tracking-wider">
            {TYPE_LABEL[sim_type]}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {needs_review && <NeedsReviewBadge count={not_ok_count} />}
          {is_overridden && (
            <Badge className="bg-orange-500 hover:bg-orange-600 text-xs">
              personalizat
            </Badge>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap gap-1.5">
        <StatPill icon={TrendingUp} label="rating mediu" value={stats.avg || "—"} accent={stats.avg >= 4} />
        <StatPill icon={Sparkles} label="evaluate" value={stats.count} />
        <StatPill icon={Lightbulb} label="5★" value={`${stats.count_5} (${pct5}%)`} accent={pct5 >= 50} />
        <StatPill icon={Pin} label="pinned" value={stats.count_pinned} />
      </div>

      {/* Editor */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-400 uppercase tracking-wide">
          Anchor (1-2 propoziții, doar culori și materiale)
        </label>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          className="bg-zinc-950/60 border-white/10 text-sm font-mono resize-none focus-visible:ring-orange-500/40"
          placeholder="Wall color: ... Trim color: ..."
          data-testid={`prompt-textarea-${sim_type}-${style}`}
        />
        {is_overridden && (
          <p className="text-[11px] text-zinc-500">
            Default original: <span className="italic text-zinc-400">"{default_anchor}"</span>
          </p>
        )}
        {fmtDate && (
          <p className="text-[11px] text-zinc-500">
            Ultima modificare: {fmtDate} de {updated_by}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid={`prompt-save-${sim_type}-${style}`}
        >
          {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
          Salvează
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={autoSuggest}
          disabled={suggesting}
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
          data-testid={`prompt-suggest-${sim_type}-${style}`}
        >
          {suggesting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
          Sugerează cu AI
        </Button>
        {is_overridden && (
          <Button
            size="sm"
            variant="ghost"
            onClick={resetDefault}
            disabled={resetting}
            className="text-zinc-400 hover:text-white"
            data-testid={`prompt-reset-${sim_type}-${style}`}
          >
            {resetting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RotateCcw size={14} className="mr-1.5" />}
            Default
          </Button>
        )}
      </div>

      {/* Suggestion dialog */}
      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="text-emerald-400" size={18} />
              Sugestie AI pentru {STYLE_LABEL[style]} / {TYPE_LABEL[sim_type]}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Gemini analizează rating-urile reale și propune un anchor mai bun.
            </DialogDescription>
          </DialogHeader>
          {suggesting && (
            <div className="flex items-center gap-3 py-12 justify-center">
              <Loader2 className="animate-spin text-emerald-400" size={24} />
              <span className="text-zinc-300">Se analizează rating-urile...</span>
            </div>
          )}
          {suggestion && !suggesting && (
            <div className="space-y-4">
              <div className="rounded-lg border border-zinc-700 bg-zinc-900/50 p-4">
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                  Curent
                </p>
                <p className="text-sm text-zinc-300 italic">"{suggestion.current_anchor}"</p>
              </div>
              <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4">
                <p className="text-[11px] text-emerald-400 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                  <Sparkles size={11} /> Sugerat de AI
                </p>
                <p className="text-sm text-white font-medium">"{suggestion.suggested_anchor}"</p>
              </div>
              {suggestion.reasoning && (
                <div className="rounded-lg border border-white/5 bg-zinc-900/30 p-3">
                  <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-1">
                    De ce
                  </p>
                  <p className="text-xs text-zinc-300 leading-relaxed">{suggestion.reasoning}</p>
                </div>
              )}
              <p className="text-[11px] text-zinc-500">
                Bazat pe {suggestion.samples_best} render-uri 4-5★ și {suggestion.samples_worst} render-uri 1-2★ existente.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setSuggestOpen(false)}
              className="text-zinc-400"
              data-testid={`prompt-suggest-cancel-${sim_type}-${style}`}
            >
              Respinge
            </Button>
            <Button
              onClick={applySuggestion}
              disabled={!suggestion}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid={`prompt-suggest-apply-${sim_type}-${style}`}
            >
              Aplică în editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function PromptManager() {
  const [items, setItems] = useState([]);
  const [issues, setIssues] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/prompts"),
        api.get("/admin/insights/issues"),
      ]);
      setItems(a.data.items || []);
      setIssues(b.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare încărcare");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const interior = items.filter((i) => i.sim_type === "INTERIOR");
  const exterior = items.filter((i) => i.sim_type === "EXTERIOR");

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-orange-500" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="prompt-manager">
      <div className="rounded-xl border border-white/10 bg-zinc-900/30 p-5">
        <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
          <Sparkles className="text-orange-500" size={20} />
          Auto-tuning prompt AI
        </h2>
        <p className="text-sm text-zinc-400 leading-relaxed">
          Anchor-urile sunt fragmente scurte din promptul Flux Kontext care
          definesc culorile și materialele pentru fiecare combinație de
          tip × stil. Editează manual sau cere AI să sugereze pe baza
          rating-urilor reale primite. Modificările se aplică instant la
          generările următoare.
        </p>
      </div>

      <IssueHeatmap issues={issues} />

      <Tabs defaultValue="INTERIOR">
        <TabsList className="bg-zinc-900/40 border border-white/10 p-1">
          <TabsTrigger
            value="INTERIOR"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
            data-testid="prompt-tab-interior"
          >
            Interior
          </TabsTrigger>
          <TabsTrigger
            value="EXTERIOR"
            className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"
            data-testid="prompt-tab-exterior"
          >
            Exterior
          </TabsTrigger>
        </TabsList>
        <TabsContent value="INTERIOR" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {interior.map((it) => (
              <AnchorCard key={it.key} item={it} onRefresh={refresh} />
            ))}
          </div>
        </TabsContent>
        <TabsContent value="EXTERIOR" className="mt-6">
          <div className="grid md:grid-cols-2 gap-4">
            {exterior.map((it) => (
              <AnchorCard key={it.key} item={it} onRefresh={refresh} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
